import { useRef, useEffect, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { NetworkData, VariableType } from './types';

mapboxgl.accessToken =
  (window as unknown as Record<string, Record<string, string>>)._env_?.VITE_MAPBOX_TOKEN ||
  import.meta.env.VITE_MAPBOX_TOKEN ||
  '';

const COLOR_RANGES: Record<string, [number, string][]> = {
  pressure_bar: [
    [2, '#0000FF'], [4, '#0044FF'], [5.5, '#0088FF'], [6.5, '#00CCFF'],
    [7.5, '#00FFCC'], [8.5, '#00FF66'], [9.5, '#88FF00'], [10.5, '#CCFF00'],
    [11.5, '#FFCC00'], [12.5, '#FF8800'], [13.5, '#FF4400'], [14.5, '#FF0000'],
  ],
  temperature_c: [
    [59, '#0000FF'], [64, '#0044FF'], [69, '#0088FF'], [74, '#00CCFF'],
    [79, '#00FFCC'], [84, '#00FF66'], [89, '#88FF00'], [94, '#CCFF00'],
    [99, '#FFCC00'], [104, '#FF8800'], [109, '#FF0000'],
  ],
  altitude: [
    [313, '#0000FF'], [325, '#0066FF'], [337, '#00CCFF'], [345, '#00FF66'],
    [352, '#88FF00'], [358, '#FFCC00'], [366, '#FF0000'],
  ],
  pressure_without_altitude_bar: [
    [2, '#0000FF'], [4, '#0044FF'], [6, '#0088FF'], [7, '#00CCFF'],
    [8.5, '#00FFCC'], [9.5, '#00FF66'], [10.5, '#88FF00'], [11.5, '#CCFF00'],
    [12.5, '#FFCC00'], [13.5, '#FF8800'], [14.5, '#FF0000'],
  ],
  speed_flow: [
    [0, '#0000FF'], [0.3, '#0066FF'], [0.6, '#00CCFF'], [0.9, '#00FF66'],
    [1.2, '#88FF00'], [1.5, '#FFCC00'], [2.0, '#FF8800'], [3.0, '#FF0000'],
  ],
  net_flow: [
    [0, '#0000FF'], [2, '#0066FF'], [5, '#00CCFF'], [10, '#00FF66'],
    [20, '#88FF00'], [40, '#FFCC00'], [80, '#FF8800'], [150, '#FF0000'],
  ],
  delta_pressure_bar: [
    [0, '#0000FF'], [0.5, '#0066FF'], [1, '#00CCFF'], [2, '#00FF66'],
    [3, '#88FF00'], [5, '#FFCC00'], [8, '#FF8800'], [12, '#FF0000'],
  ],
  gradient_loss: [
    [0, '#0000FF'], [50, '#0066FF'], [100, '#00CCFF'], [150, '#00FF66'],
    [200, '#88FF00'], [300, '#FFCC00'], [500, '#FF8800'], [1000, '#FF0000'],
  ],
  nominal_diameter: [
    [0.05, '#0000FF'], [0.1, '#0066FF'], [0.15, '#00CCFF'], [0.2, '#00FF66'],
    [0.25, '#88FF00'], [0.3, '#FFCC00'], [0.4, '#FF8800'], [0.5, '#FF0000'],
  ],
  available_flow: [
    [0, '#0000FF'], [1, '#0066FF'], [3, '#00CCFF'], [5, '#00FF66'],
    [10, '#88FF00'], [20, '#FFCC00'], [50, '#FF8800'], [100, '#FF0000'],
  ],
  available_power: [
    [0, '#0000FF'], [50, '#0066FF'], [100, '#00CCFF'], [200, '#00FF66'],
    [500, '#88FF00'], [1000, '#FFCC00'], [2000, '#FF8800'], [5000, '#FF0000'],
  ],
  weighted_deltaT: [
    [0, '#0000FF'], [5, '#0066FF'], [10, '#00CCFF'], [15, '#00FF66'],
    [20, '#88FF00'], [30, '#FFCC00'], [40, '#FF8800'], [60, '#FF0000'],
  ],
  net_volumetric_flow: [
    [0, '#0000FF'], [0.002, '#0066FF'], [0.005, '#00CCFF'], [0.01, '#00FF66'],
    [0.02, '#88FF00'], [0.05, '#FFCC00'], [0.1, '#FF8800'], [0.2, '#FF0000'],
  ],
  global_optimized_diameter: [
    [0.05, '#0000FF'], [0.1, '#0066FF'], [0.15, '#00CCFF'], [0.2, '#00FF66'],
    [0.25, '#88FF00'], [0.3, '#FFCC00'], [0.4, '#FF8800'], [0.5, '#FF0000'],
  ],
};

function buildColorExpression(variable: VariableType): mapboxgl.Expression {
  const colors = COLOR_RANGES[variable] || COLOR_RANGES.pressure_bar;
  const steps: (string | number)[] = [];
  colors.forEach(([val, color]) => {
    steps.push(val, color);
  });
  // coalesce: use the variable value, or fallback to 0 (which will get the lowest color)
  return ['interpolate', ['linear'], ['coalesce', ['get', variable], 0], ...steps];
}

interface MapViewProps {
  data: NetworkData;
  nodeVariable: VariableType;
  pipeVariable: VariableType;
  nodesVisible: boolean;
  pipesVisible: boolean;
  searchQuery: string;
}

export default function MapView({
  data,
  nodeVariable,
  pipeVariable,
  nodesVisible,
  pipesVisible,
  searchQuery,
}: MapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const popupRef = useRef<mapboxgl.Popup | null>(null);

  // Initialize map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    if (!mapboxgl.accessToken) {
      containerRef.current.innerHTML = `
        <div style="display:flex;align-items:center;justify-content:center;height:100%;flex-direction:column;gap:8px;color:#666">
          <span style="font-size:32px">🗺️</span>
          <p style="margin:0;font-size:14px">Token Mapbox manquant</p>
          <p style="margin:0;font-size:12px;color:#999">Ajoutez VITE_MAPBOX_TOKEN dans app/front/.env.local</p>
        </div>`;
      return;
    }

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [6.81, 47.50],
      zoom: 13,
    });

    map.addControl(new mapboxgl.NavigationControl(), 'bottom-left');

    map.on('load', () => {
      // Add pipes source + layer
      map.addSource('pipes', { type: 'geojson', data: data.pipes });
      map.addLayer({
        id: 'pipes-layer',
        type: 'line',
        source: 'pipes',
        paint: {
          'line-color': buildColorExpression(pipeVariable),
          'line-width': ['interpolate', ['linear'], ['get', 'diameter'], 0.05, 2, 0.5, 6],
          'line-opacity': 0.9,
        },
      });

      // Add nodes source + layer
      map.addSource('nodes', { type: 'geojson', data: data.nodes });
      map.addLayer({
        id: 'nodes-layer',
        type: 'circle',
        source: 'nodes',
        paint: {
          'circle-color': buildColorExpression(nodeVariable),
          'circle-radius': 4,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#333',
        },
      });

      // Popup on hover
      const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false });
      popupRef.current = popup;

      map.on('mouseenter', 'nodes-layer', (e) => {
        map.getCanvas().style.cursor = 'pointer';
        const feat = e.features?.[0];
        if (!feat) return;
        const props = feat.properties as Record<string, unknown>;
        const coords = (feat.geometry as GeoJSON.Point).coordinates as [number, number];
        popup
          .setLngLat(coords)
          .setHTML(
            `<strong>${props.name}</strong><br/>` +
            `Pression: ${props.pressure_bar} bar<br/>` +
            `Température: ${props.temperature_c ?? '—'}°C`
          )
          .addTo(map);
      });

      map.on('mouseleave', 'nodes-layer', () => {
        map.getCanvas().style.cursor = '';
        popup.remove();
      });

      map.on('mouseenter', 'pipes-layer', (e) => {
        map.getCanvas().style.cursor = 'pointer';
        const feat = e.features?.[0];
        if (!feat) return;
        const props = feat.properties as Record<string, unknown>;
        popup
          .setLngLat(e.lngLat)
          .setHTML(
            `<strong>${props.name}</strong><br/>` +
            `Pression moy.: ${props.pressure_bar} bar<br/>` +
            `Vitesse: ${props.speed} m/s<br/>` +
            `DN: ${Math.round(Number(props.diameter) * 1000)} mm`
          )
          .addTo(map);
      });

      map.on('mouseleave', 'pipes-layer', () => {
        map.getCanvas().style.cursor = '';
        popup.remove();
      });
    });

    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update paint on variable change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    try {
      if (map.getLayer('nodes-layer')) {
        map.setPaintProperty('nodes-layer', 'circle-color', buildColorExpression(nodeVariable));
      }
    } catch { /* layer not ready yet, will be set on init */ }
  }, [nodeVariable]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    try {
      if (map.getLayer('pipes-layer')) {
        map.setPaintProperty('pipes-layer', 'line-color', buildColorExpression(pipeVariable));
      }
    } catch { /* layer not ready yet */ }
  }, [pipeVariable]);

  // Toggle visibility
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    if (map.getLayer('nodes-layer')) {
      map.setLayoutProperty('nodes-layer', 'visibility', nodesVisible ? 'visible' : 'none');
    }
  }, [nodesVisible]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    if (map.getLayer('pipes-layer')) {
      map.setLayoutProperty('pipes-layer', 'visibility', pipesVisible ? 'visible' : 'none');
    }
  }, [pipesVisible]);

  // Search: fly to matching entity
  const flyToEntity = useCallback((query: string) => {
    if (!query || !mapRef.current) return;
    const lowerQ = query.toLowerCase();
    const nodeMatch = data.nodes.features.find(
      (f) => f.properties.name.toLowerCase().includes(lowerQ)
    );
    if (nodeMatch) {
      const [lng, lat] = nodeMatch.geometry.coordinates;
      mapRef.current.flyTo({ center: [lng, lat], zoom: 16 });
      return;
    }
    const pipeMatch = data.pipes.features.find(
      (f) => f.properties.name.toLowerCase().includes(lowerQ)
    );
    if (pipeMatch && pipeMatch.geometry.coordinates.length > 0) {
      const mid = pipeMatch.geometry.coordinates[Math.floor(pipeMatch.geometry.coordinates.length / 2)];
      mapRef.current.flyTo({ center: [mid[0], mid[1]], zoom: 16 });
    }
  }, [data]);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      flyToEntity(searchQuery);
    }
  }, [searchQuery, flyToEntity]);

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }} />
  );
}
